import { useEffect } from "react"

import {
  type BusinessAddress,
  type PartialBusinessAddress,
  CountryEnum,
} from "@boundless/bl-api-client/customer/models"
import { CountryEnumOptions } from "@boundless/bl-api-client/customer/models/CountryEnumLabels"
import { StateEnumOptions } from "@boundless/bl-api-client/customer/models/StateEnumLabels"
import {
  customerCompanyRetrieve,
  getCustomerBusinessAddressRetrieveQueryKey,
  getCustomerCompanyRetrieveQueryKey,
  getCustomerPartialBusinessAddressRetrieveQueryKey,
  useCustomerBusinessAddressUpdate,
  useCustomerPartialBusinessAddressPartialUpdate,
} from "@boundless/bl-api-client/customer/services"
import {
  CustomerBusinessAddressUpdateBody,
  CustomerPartialBusinessAddressPartialUpdateBody,
} from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm, useFormContext } from "react-hook-form"

import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { AutosaveStatus } from "@/components/form-elements/AutosaveStatus"
import { FormCheckbox } from "@/components/form-elements/FormCheckbox"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormInputPhone } from "@/components/form-elements/FormInputPhone"
import { FormInputZipCode } from "@/components/form-elements/FormInputZipCode"
import { FormSection } from "@/components/form-elements/FormSection"
import { FormSelect } from "@/components/form-elements/FormSelect"
import { LoadingButton } from "@/components/ui/button"
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { businessAddressErrorMap } from "@/lib/errors"
import { CompanyFlowContext } from "@/lib/flows/company"
import { useAutosaveDraft } from "@/lib/forms/useAutosaveDraft"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { z } from "zod"

type BusinessAddressFormSchema = z.infer<
  typeof CustomerBusinessAddressUpdateBody
>

/**
 * Returns initial form data for the business address form.
 *
 * Uses a priority cascade to populate form fields:
 * 1. Full business address
 * 2. Partial business address
 * 3. Empty/default values
 *
 * Enum fields are coerced to empty strings when the source lacks the value,
 * ensuring the form receives valid enum values for validation.
 */
function getInitialAddressFormData(
  businessAddress: BusinessAddress | null | undefined,
  partialBusinessAddress: PartialBusinessAddress | null | undefined,
): BusinessAddressFormSchema {
  const source = businessAddress || partialBusinessAddress

  if (!source) {
    return {
      incorporatedCountry: "" as BusinessAddress["incorporatedCountry"],
      incorporatedState: "" as BusinessAddress["incorporatedState"],
      incorporatedCity: "",
      incorporatedZipCode: "",
      incorporatedAddress: "",
      incorporatedPhone: "",
      isPhysicalAddressSame: false,
      physicalCountry: "" as BusinessAddress["physicalCountry"],
      physicalState: "" as BusinessAddress["physicalState"],
      physicalCity: "",
      physicalZipCode: "",
      physicalAddress: "",
      physicalPhone: "",
    }
  }

  return {
    incorporatedCountry:
      source.incorporatedCountry ||
      ("" as BusinessAddress["incorporatedCountry"]),
    incorporatedState:
      source.incorporatedState || ("" as BusinessAddress["incorporatedState"]),
    incorporatedCity: source.incorporatedCity ?? "",
    incorporatedZipCode: source.incorporatedZipCode ?? "",
    incorporatedAddress: source.incorporatedAddress ?? "",
    incorporatedPhone: source.incorporatedPhone ?? "",
    isPhysicalAddressSame: source.isPhysicalAddressSame ?? false,
    physicalCountry:
      source.physicalCountry || ("" as BusinessAddress["physicalCountry"]),
    physicalState:
      source.physicalState || ("" as BusinessAddress["physicalState"]),
    physicalCity: source.physicalCity ?? "",
    physicalZipCode: source.physicalZipCode ?? "",
    physicalAddress: source.physicalAddress ?? "",
    physicalPhone: source.physicalPhone ?? "",
  }
}

/**
 * Returns default values for physical address fields using priority cascade.
 * Falls back to partial values if full address is not available.
 */
function getPhysicalDefaults(
  businessAddress: BusinessAddress | undefined,
  partialBusinessAddress: PartialBusinessAddress | undefined,
) {
  return {
    physicalCountry:
      businessAddress?.physicalCountry ||
      partialBusinessAddress?.physicalCountry ||
      "",
    physicalState:
      businessAddress?.physicalState ||
      partialBusinessAddress?.physicalState ||
      "",
    physicalCity:
      businessAddress?.physicalCity ??
      partialBusinessAddress?.physicalCity ??
      "",
    physicalZipCode:
      businessAddress?.physicalZipCode ??
      partialBusinessAddress?.physicalZipCode ??
      "",
    physicalAddress:
      businessAddress?.physicalAddress ??
      partialBusinessAddress?.physicalAddress ??
      "",
    physicalPhone:
      businessAddress?.physicalPhone ??
      partialBusinessAddress?.physicalPhone ??
      "",
  } as const
}

interface Props {
  companyUuid: string
  businessAddress: BusinessAddress | undefined
  partialBusinessAddress: PartialBusinessAddress | undefined
}

export function BusinessAddressForm({
  companyUuid,
  businessAddress,
  partialBusinessAddress,
}: Props) {
  const queryClient = useQueryClient()
  const companyQueryKey = getCustomerCompanyRetrieveQueryKey(companyUuid)
  const flowActorRef = CompanyFlowContext.useActorRef()

  const goBack = () => flowActorRef.send({ type: "back" })
  const goNext = () => flowActorRef.send({ type: "next" })

  const initialValues = getInitialAddressFormData(
    businessAddress,
    partialBusinessAddress,
  )

  const form = useForm<BusinessAddressFormSchema>({
    resolver: zodResolver(CustomerBusinessAddressUpdateBody, {
      errorMap: businessAddressErrorMap,
    }),
    resetOptions: { keepDirtyValues: true },
    defaultValues: initialValues,
  })

  const { mutate: update, isPending } = useCustomerBusinessAddressUpdate({
    mutation: {
      onSuccess: async (address: BusinessAddress) => {
        const queryKey = getCustomerBusinessAddressRetrieveQueryKey(companyUuid)
        queryClient.setQueryData(queryKey, address)

        await queryClient.invalidateQueries({ queryKey: companyQueryKey })
        const updatedCompanyData = await customerCompanyRetrieve(companyUuid)

        flowActorRef.send({
          type: "setRequiresExtraDetails",
          requiresExtraDetails: updatedCompanyData.requiresExtraDetails,
        })

        goNext()
      },
      onError: (error: ErrorType<ErrorResponseType>) => {
        applyErrorsToForm(form.setError, error)
      },
    },
  })

  const { mutateAsync: updateDraft } =
    useCustomerPartialBusinessAddressPartialUpdate({
      mutation: {
        meta: { shouldDisplayErrorToast: false },
        onSuccess: (draft: PartialBusinessAddress) =>
          queryClient.setQueryData(
            getCustomerPartialBusinessAddressRetrieveQueryKey(companyUuid),
            draft,
          ),
      },
    })

  // The draft exists only until this screen is completed, so a screen that
  // already has its real address has nothing to autosave into. The route holds
  // the form back until that lookup settles, so an undefined address here
  // means absent rather than not yet known.
  const autosave = useAutosaveDraft({
    form,
    initialValues,
    schema: CustomerPartialBusinessAddressPartialUpdateBody,
    enabled: !businessAddress,
    save: (data) => updateDraft({ companyUuid, data }),
  })

  // Busy while submitting waits out a draft save, so it cannot submit twice.
  const isSubmitting = isPending || form.formState.isSubmitting

  const handleSubmit = async (addressFormData: BusinessAddressFormSchema) => {
    // Saving the real address deletes the draft. A save already sent would
    // land after that and recreate one for a completed screen.
    await autosave.cancel()
    update({ companyUuid, data: addressFormData })
  }

  const isPhysicalAddressSame = form.watch("isPhysicalAddressSame")

  const addressMapping = {
    incorporatedCountry: "physicalCountry",
    incorporatedState: "physicalState",
    incorporatedCity: "physicalCity",
    incorporatedZipCode: "physicalZipCode",
    incorporatedAddress: "physicalAddress",
    incorporatedPhone: "physicalPhone",
  } as const

  const incorporatedFields = [
    "incorporatedCountry",
    "incorporatedState",
    "incorporatedCity",
    "incorporatedZipCode",
    "incorporatedAddress",
    "incorporatedPhone",
  ] as const

  const watchedIncorporatedValues = form.watch(incorporatedFields)

  const physicalFields = [
    "physicalCountry",
    "physicalState",
    "physicalCity",
    "physicalZipCode",
    "physicalAddress",
    "physicalPhone",
  ] as const

  const physicalDefaults = getPhysicalDefaults(
    businessAddress,
    partialBusinessAddress,
  )

  useEffect(() => {
    if (isPhysicalAddressSame) {
      incorporatedFields.forEach((f, index) => {
        const physicalField = addressMapping[f as keyof typeof addressMapping]
        form.setValue(physicalField, watchedIncorporatedValues[index])
        form.clearErrors(physicalField)
      })
    } else {
      physicalFields.forEach((f) => {
        form.resetField(f, { defaultValue: physicalDefaults[f] })
        form.clearErrors(f)
      })
    }
  }, [isPhysicalAddressSame, ...watchedIncorporatedValues])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Incorporated Business Address</CardTitle>
            <CardAction>
              <AutosaveStatus status={autosave.status} />
            </CardAction>
          </CardHeader>

          <CardContent className="@container/form">
            <IncorporatedFields />
          </CardContent>

          <CardContent>
            <FormCheckbox
              id="same-address"
              name="isPhysicalAddressSame"
              label="Physical Address same as Incorporated Address"
            />
          </CardContent>

          {!isPhysicalAddressSame && (
            <>
              <CardHeader>
                <CardTitle>Physical Business Address</CardTitle>
              </CardHeader>
              <CardContent className="@container/form">
                <PhysicalFields />
              </CardContent>
            </>
          )}

          <CardFooter className="w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <ButtonBack onClick={() => goBack()} className="w-full sm:w-auto" />
            {isSubmitting ? (
              <LoadingButton className="w-full sm:w-auto" />
            ) : (
              <ButtonNext type="submit" className="w-full sm:w-auto" />
            )}
          </CardFooter>
        </Card>
      </form>
    </Form>
  )
}

function IncorporatedFields() {
  const form = useFormContext()

  const incorporatedCountry =
    form.watch("incorporatedCountry") || CountryEnum.US
  const incorporatedStateOptions = StateEnumOptions.filter(({ value }) =>
    value.startsWith(incorporatedCountry),
  )

  return (
    <FormSection data-testid="incorporated-address">
      <FormSelect
        className="col-span-2"
        name="incorporatedCountry"
        options={CountryEnumOptions}
        label="Country"
        placeholder="Country"
        onChange={() => {
          form.setValue("incorporatedState", "")
        }}
      />
      <FormSelect
        name="incorporatedState"
        options={incorporatedStateOptions}
        label="State/Province"
        placeholder="State/Province"
      />
      <FormInput name="incorporatedCity" label="City" placeholder="City" />
      <FormInputZipCode
        name="incorporatedZipCode"
        countryField="incorporatedCountry"
      />
      <FormInputPhone
        name="incorporatedPhone"
        label="Phone Number"
        placeholder="Phone Number"
      />
      <FormInput
        className="col-span-2"
        name="incorporatedAddress"
        label="Street Address"
        placeholder="Street Address"
      />
    </FormSection>
  )
}

function PhysicalFields() {
  const form = useFormContext()

  const physicalCountry = form.watch("physicalCountry") || CountryEnum.US
  const physicalStateOptions = StateEnumOptions.filter(({ value }) =>
    value.startsWith(physicalCountry),
  )

  return (
    <FormSection data-testid="physical-address">
      <FormSelect
        className="col-span-2"
        name="physicalCountry"
        options={CountryEnumOptions}
        label="Country"
        placeholder="Country"
        onChange={() => {
          form.setValue("physicalState", "")
        }}
      />
      <FormSelect
        name="physicalState"
        options={physicalStateOptions}
        label="State/Province"
        placeholder="State/Province"
      />
      <FormInput name="physicalCity" label="City" placeholder="City" />
      <FormInputZipCode name="physicalZipCode" countryField="physicalCountry" />
      <FormInputPhone
        name="physicalPhone"
        label="Phone Number"
        placeholder="Phone Number"
      />
      <FormInput
        className="col-span-2"
        label="Street Address"
        name="physicalAddress"
        placeholder="Street Address"
      />
    </FormSection>
  )
}
